import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Upload, Trash2, Star, ExternalLink, X, ChevronLeft, ChevronRight, Image as ImageIcon, Heart, MessageSquare, Bookmark, User, GripVertical } from 'lucide-react';
import { adminApi } from '../api';
import AddPhotosDialog from '../components/AddPhotosDialog';
import BrandLogo from '../components/BrandLogo';
import InlineTitleEdit from '../components/InlineTitleEdit';
import { toast } from 'sonner';

const AdminSubgallery = () => {
  const { clientSlug, gallerySlug, subgallerySlug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null); // { subgallery, gallery, client, photos }
  const [addOpen, setAddOpen] = useState(false);
  const [openIdx, setOpenIdx] = useState(-1);
  const [dragIdx, setDragIdx] = useState(-1);
  const [overIdx, setOverIdx] = useState(-1);
  const [savingOrder, setSavingOrder] = useState(false);

  // Fetch subgallery by finding its id via gallery slugs (we need the id, not the slug for backend endpoint)
  const load = async () => {
    try {
      // Reach through admin API: get client -> gallery -> subgallery id
      const c = await adminApi.getClient(clientSlug);
      const g = c.galleries.find(x => x.slug === gallerySlug);
      if (!g) { toast.error('Lavoro non trovato'); nav(`/admin/clienti/${clientSlug}`); return; }
      const subs = await adminApi.listSubgalleries(g.id);
      const s = subs.find(x => x.slug === subgallerySlug);
      if (!s) { toast.error('Sotto-galleria non trovata'); nav(`/admin/clienti/${clientSlug}/lavori/${gallerySlug}`); return; }
      const full = await adminApi.getSubgallery(s.id);
      setData({ ...full, clientSlug: c.slug, gallerySlug: g.slug });
    } catch (err) { toast.error('Errore caricamento'); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [clientSlug, gallerySlug, subgallerySlug]);

  const removePhoto = async (p) => {
    if (!window.confirm(`Eliminare la foto "${p.filename}"?`)) return;
    try { await adminApi.deletePhoto(p.id); toast.success('Foto eliminata'); await load(); }
    catch { toast.error('Errore eliminazione'); }
  };

  const setCover = async (p) => {
    try { await adminApi.setSubgalleryCover(data.subgallery.id, p.id); toast.success('Cover sotto-galleria aggiornata'); await load(); }
    catch { toast.error('Errore'); }
  };

  const setGalleryCoverFn = async (p) => {
    try { await adminApi.setGalleryCover(data.gallery.id, p.id); toast.success('Cover del lavoro aggiornata'); await load(); }
    catch { toast.error('Errore'); }
  };

  const setClientCoverFn = async (p) => {
    try { await adminApi.setClientCover(data.client.id, p.id); toast.success('Cover del cliente aggiornata'); await load(); }
    catch { toast.error('Errore'); }
  };

  // Drag & drop reorder
  const onDragStart = (e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(idx)); } catch {}
  };
  const onDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (overIdx !== idx) setOverIdx(idx);
  };
  const onDragEnd = () => { setDragIdx(-1); setOverIdx(-1); };
  const onDrop = async (e, idx) => {
    e.preventDefault();
    const from = dragIdx;
    setDragIdx(-1); setOverIdx(-1);
    if (from < 0 || from === idx) return;
    const arr = [...data.photos];
    const [moved] = arr.splice(from, 1);
    arr.splice(idx, 0, moved);
    setData(d => ({ ...d, photos: arr }));
    // Persist to backend
    setSavingOrder(true);
    try {
      await adminApi.reorderSubgalleryPhotos(data.subgallery.id, arr.map(p => p.id));
      toast.success('Ordine foto aggiornato');
    } catch {
      toast.error('Errore riordino');
      await load();
    } finally { setSavingOrder(false); }
  };

  const onKey = (e) => {
    if (openIdx < 0) return;
    if (e.key === 'Escape') setOpenIdx(-1);
    if (e.key === 'ArrowRight') setOpenIdx(i => Math.min(data.photos.length - 1, i + 1));
    if (e.key === 'ArrowLeft') setOpenIdx(i => Math.max(0, i - 1));
  };
  useEffect(() => {
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line
  }, [openIdx, data]);

  if (!data) return null;
  const { gallery, subgallery, photos } = data;

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-white">
      <header className="border-b border-white/10 bg-black/40 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center gap-4">
          <Link to={`/admin/clienti/${clientSlug}/lavori/${gallerySlug}`} className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white"><ArrowLeft className="w-4 h-4" /> {gallery.title}</Link>
          <div className="h-4 w-px bg-white/10" />
          <BrandLogo size="sm" hideText />
          <div className="min-w-0 ml-2">
            <p className="text-[10px] uppercase tracking-widest text-[#C9A961]">{gallery.title} · Sotto-galleria admin</p>
            <h1 className="font-display text-lg md:text-xl truncate">{subgallery.title}</h1>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <a href={`/c/${clientSlug}/lavori/${gallerySlug}/${subgallerySlug}`} target="_blank" rel="noreferrer" className="hidden sm:inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-2 rounded-full"><ExternalLink className="w-3.5 h-3.5" /> Anteprima cliente</a>
            <button onClick={()=>setAddOpen(true)} className="inline-flex items-center gap-1.5 bg-[#C9A961] hover:bg-white text-black text-xs font-semibold px-4 py-2 rounded-full transition"><Upload className="w-3.5 h-3.5" /> Aggiungi foto</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-[#C9A961] uppercase tracking-[0.25em] text-[11px] font-semibold">Gestione foto</p>
            <h2 className="font-display text-3xl md:text-4xl mt-1">
              <InlineTitleEdit
                value={subgallery.title}
                dataTestId="admin-subgallery-title"
                onSave={async (t) => { await adminApi.updateSubgallery(subgallery.id, { title: t }); toast.success('Titolo sotto-galleria aggiornato'); await load(); }}
              />
            </h2>
            <p className="text-slate-400 text-sm mt-1">
              {photos.length} {photos.length === 1 ? 'foto' : 'foto'} caricate
              <span className="mx-2 text-slate-600">·</span>
              <span className="inline-flex items-center gap-1 text-[#C9A961]"><GripVertical className="w-3.5 h-3.5" /> Trascina per riordinare</span>
              {savingOrder && <span className="ml-2 text-slate-500 italic">salvataggio ordine…</span>}
            </p>
          </div>
        </div>

        {photos.length === 0 ? (
          <div className="bg-white/5 border-2 border-dashed border-white/10 rounded-2xl p-16 text-center">
            <ImageIcon className="w-10 h-10 text-[#C9A961] mx-auto mb-3" />
            <h3 className="font-semibold text-lg mb-1">Nessuna foto ancora</h3>
            <p className="text-slate-400 mb-6 text-sm">Carica le prime foto di questa sotto-galleria.</p>
            <button onClick={()=>setAddOpen(true)} className="inline-flex items-center gap-2 bg-[#C9A961] hover:bg-white text-black font-semibold px-5 py-2.5 rounded-full transition"><Upload className="w-4 h-4" /> Aggiungi foto</button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {photos.map((p, i) => {
              const isCover = subgallery.cover_url === p.url;
              const isGalleryCover = gallery.cover_url === p.url;
              const isClientCover = data.client && data.client.cover_url === p.url;
              const isDragging = dragIdx === i;
              const isOver = overIdx === i && dragIdx !== -1 && dragIdx !== i;
              return (
                <div
                  key={p.id}
                  draggable
                  onDragStart={(e)=>onDragStart(e, i)}
                  onDragOver={(e)=>onDragOver(e, i)}
                  onDrop={(e)=>onDrop(e, i)}
                  onDragEnd={onDragEnd}
                  className={`group relative rounded-xl overflow-hidden bg-white/5 border aspect-square transition-all ${isDragging ? 'opacity-40 scale-95' : ''} ${isOver ? 'border-[#C9A961] ring-2 ring-[#C9A961]/50 scale-[1.02]' : 'border-white/10'}`}
                >
                  <img src={p.url} alt={p.filename} className="w-full h-full object-cover cursor-zoom-in pointer-events-none select-none" onClick={()=>setOpenIdx(i)} loading="lazy" draggable={false} />
                  {/* Overlay click layer to allow lightbox open (below drag handle) */}
                  <div className="absolute inset-0 cursor-zoom-in" onClick={()=>setOpenIdx(i)} />
                  {/* Drag handle indicator top-right */}
                  <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/60 backdrop-blur text-white grid place-items-center opacity-0 group-hover:opacity-100 transition cursor-grab active:cursor-grabbing pointer-events-none" title="Trascina per riordinare">
                    <GripVertical className="w-3.5 h-3.5" />
                  </div>
                  {isCover && (
                    <div className="absolute top-2 left-2 inline-flex items-center gap-1 bg-[#C9A961] text-black text-[10px] font-bold px-2 py-0.5 rounded-full pointer-events-none">
                      <Star className="w-3 h-3 fill-current" /> COVER
                    </div>
                  )}
                  {isGalleryCover && (
                    <div className={`absolute ${isCover ? 'top-8' : 'top-2'} left-2 inline-flex items-center gap-1 bg-white text-black text-[10px] font-bold px-2 py-0.5 rounded-full pointer-events-none`}>
                      <Bookmark className="w-3 h-3 fill-current" /> LAVORO
                    </div>
                  )}
                  {isClientCover && (
                    <div className={`absolute ${(isCover && isGalleryCover) ? 'top-14' : (isCover || isGalleryCover) ? 'top-8' : 'top-2'} left-2 inline-flex items-center gap-1 bg-rose-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full pointer-events-none`}>
                      <User className="w-3 h-3" /> CLIENTE
                    </div>
                  )}
                  {(p.likes > 0 || (p.comments && p.comments.length > 0)) && (
                    <div className="absolute bottom-2 left-2 flex items-center gap-1.5 text-[10px] font-medium pointer-events-none">
                      {p.likes > 0 && <span className="inline-flex items-center gap-0.5 bg-rose-500/90 text-white px-1.5 py-0.5 rounded-full"><Heart className="w-2.5 h-2.5 fill-current" /> {p.likes}</span>}
                      {p.comments && p.comments.length > 0 && <span className="inline-flex items-center gap-0.5 bg-black/70 text-[#C9A961] px-1.5 py-0.5 rounded-full"><MessageSquare className="w-2.5 h-2.5" /> {p.comments.length}</span>}
                    </div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 p-2 opacity-0 group-hover:opacity-100 transition bg-gradient-to-t from-black/85 to-transparent flex items-center justify-end gap-1.5 z-10">
                    {!isCover && (
                      <button data-testid={`set-subgallery-cover-${p.id}`} onClick={(e)=>{ e.stopPropagation(); setCover(p); }} className="w-7 h-7 rounded-full bg-white/20 hover:bg-[#C9A961] hover:text-black text-white grid place-items-center" title="Imposta come cover sotto-galleria"><Star className="w-3.5 h-3.5" /></button>
                    )}
                    {!isGalleryCover && (
                      <button data-testid={`set-gallery-cover-${p.id}`} onClick={(e)=>{ e.stopPropagation(); setGalleryCoverFn(p); }} className="w-7 h-7 rounded-full bg-white/20 hover:bg-white hover:text-black text-white grid place-items-center" title="Imposta come cover del lavoro"><Bookmark className="w-3.5 h-3.5" /></button>
                    )}
                    {!isClientCover && (
                      <button data-testid={`set-client-cover-${p.id}`} onClick={(e)=>{ e.stopPropagation(); setClientCoverFn(p); }} className="w-7 h-7 rounded-full bg-white/20 hover:bg-rose-500 text-white grid place-items-center" title="Imposta come cover del cliente"><User className="w-3.5 h-3.5" /></button>
                    )}
                    <button data-testid={`delete-photo-${p.id}`} onClick={(e)=>{ e.stopPropagation(); removePhoto(p); }} className="w-7 h-7 rounded-full bg-white/20 hover:bg-rose-500 text-white grid place-items-center" title="Elimina"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {openIdx >= 0 && photos[openIdx] && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
          <button onClick={()=>setOpenIdx(-1)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 text-white grid place-items-center hover:bg-white/20"><X className="w-5 h-5" /></button>
          <button disabled={openIdx===0} onClick={()=>setOpenIdx(openIdx-1)} className="absolute left-4 w-10 h-10 rounded-full bg-white/10 text-white grid place-items-center hover:bg-white/20 disabled:opacity-30"><ChevronLeft className="w-5 h-5" /></button>
          <button disabled={openIdx===photos.length-1} onClick={()=>setOpenIdx(openIdx+1)} className="absolute right-4 w-10 h-10 rounded-full bg-white/10 text-white grid place-items-center hover:bg-white/20 disabled:opacity-30"><ChevronRight className="w-5 h-5" /></button>
          <img src={photos[openIdx].url} alt={photos[openIdx].filename} className="max-h-[90vh] max-w-[95vw] object-contain rounded-lg" />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 backdrop-blur border border-white/10 rounded-full px-4 py-2 text-xs text-white flex items-center gap-3">
            <span>{photos[openIdx].filename}</span>
            <span className="text-slate-400">· {openIdx+1}/{photos.length}</span>
            {photos[openIdx].likes > 0 && <span className="inline-flex items-center gap-1 text-rose-400"><Heart className="w-3 h-3 fill-current" /> {photos[openIdx].likes}</span>}
            {photos[openIdx].comments?.length > 0 && <span className="inline-flex items-center gap-1 text-[#C9A961]"><MessageSquare className="w-3 h-3" /> {photos[openIdx].comments.length}</span>}
          </div>
        </div>
      )}

      {addOpen && (
        <AddPhotosDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          subgalleryId={subgallery.id}
          subgalleryTitle={subgallery.title}
          onAdded={load}
        />
      )}
    </div>
  );
};

export default AdminSubgallery;
